import { LOCALES } from '@aysar/contracts';

import { localizedPath } from '../localized-form/localized-form';
import { type FieldDef } from '../types/dynamic-form.types';

/** Reads a dotted path out of an arbitrary payload without assuming its shape. */
export function readPath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((value, segment) => {
    if (value === null || typeof value !== 'object') {
      return undefined;
    }

    return (value as Record<string, unknown>)[segment];
  }, source);
}

/**
 * Writes a dotted path, creating — and **copying** — the objects along the way.
 *
 * It never writes *into* a branch it did not make. An object group seeds its own
 * path with the value the server resolved, and RTK Query **freezes its cache in
 * development**, so descending into that object to write `value.name` threw
 * "Cannot assign to read only property" and the router's error boundary blanked
 * the settings screen — found by the P10.3b walk, past every spec, because a
 * spec's fixtures are ordinary literals.
 *
 * Copying is right whether or not anything is frozen: the value layer builds the
 * form's own object, and reaching into the store's would make an edit visible in
 * the cache before it was ever submitted.
 */
export function writePath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  const leaf = segments.pop();

  if (leaf === undefined) {
    return;
  }

  let cursor = target;

  for (const segment of segments) {
    const next = cursor[segment];
    cursor[segment] =
      next === null || typeof next !== 'object' || Array.isArray(next)
        ? {}
        : { ...(next as Record<string, unknown>) };
    cursor = cursor[segment] as Record<string, unknown>;
  }

  cursor[leaf] = value;
}

/**
 * The empty value for a control that has never been touched.
 *
 * A `number` defaults to **undefined, never 0**, and that is the important one.
 * Zero is a real answer — a price, an age, a retry count — so a form that
 * pre-fills it makes "the operator left this blank" indistinguishable from "the
 * operator meant zero", and a `min(0)` rule then passes on a field nobody filled
 * in. An empty numeric input is the honest representation of "not yet said".
 */
function emptyValueFor(field: FieldDef): unknown {
  switch (field.kind) {
    // A duration, a percent, an amount of money and a height are numbers too,
    // and the same rule decides them: an empty box is "not yet said", never a
    // real 0 — which for a TTL would be "expires immediately", for a rate would
    // be "free", and for a height would be nobody.
    case 'number':
    case 'duration':
    case 'percent':
    case 'money':
    case 'height':
      return undefined;
    case 'switch':
    case 'checkbox':
      return false;
    // A media field is `null`, never `''`: the value is a uuid or the absence of
    // one, and an empty string would be a third state the schema has to make an
    // exception for — and one the picker would try to resolve.
    // A `datetime` is `null` for the same reason a `date` is: the value is an
    // instant or the absence of one, and `''` would be a third state the schema
    // has to except and the control would have to narrow away again.
    case 'date':
    case 'datetime':
    case 'media':
      return null;
    case 'custom':
    case 'json':
      // A `json` field genuinely has no value until one is typed, and `''` would
      // be shown as the text `""` in the editor.
      return undefined;
    case 'multi-select':
      return [];
    case 'country-set':
      return {};
    case 'toggle-group':
    case 'key-value':
    case 'object':
      // Object-shaped: an empty map, so the control renders "no entries" rather
      // than treating `''` as a value it has to narrow. For an object GROUP it
      // is also structural — the group is written before its properties, so this
      // is the branch `value.name` is then written into. `''` here would be
      // replaced by `writePath` anyway, but only by accident.
      return {};
    default:
      // Text, textarea, password, select, radio-group, slug, colour: the empty
      // string keeps the input CONTROLLED. `undefined` hands it back to the DOM
      // mid-life and React warns about the switch on the first keystroke.
      return '';
  }
}

/**
 * The `defaultValues` for a form: one entry per field, overlaid with whatever
 * the record being edited already has.
 *
 * Translatable fields expand to one entry per locale, so an edit form opens with
 * both tabs prefilled and a create form opens with both tabs blank — never with
 * one locale missing, which is what makes RHF flip that input from uncontrolled
 * to controlled halfway through editing.
 */
export function buildDefaultValues(
  fields: readonly FieldDef[],
  initial?: unknown,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  for (const field of fields) {
    const paths =
      field.isTranslatable === true
        ? LOCALES.map((locale) => localizedPath(field.name, locale))
        : [field.name];

    for (const path of paths) {
      const existing = initial === undefined ? undefined : readPath(initial, path);

      // `undefined` means absent; `false`, `0` and `''` are real stored values
      // and must survive into the form untouched.
      writePath(values, path, existing === undefined ? emptyValueFor(field) : existing);
    }
  }

  return values;
}
